import { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { checkUserExistOrNotApi, insertOtpForExistEmailApi, updateEmailOfPanelistApi, updatePasswordForExistEmailApi, verifyIsOtpAreValidateApi } from "./surveycomplete_api";
import { toastrSuccess } from "./../../lib/toastr";
import StepOne from "./GuestUserPages/stepOne";
import StepOneHalf from "./GuestUserPages/stepOneHalf";
import StepTwo from "./GuestUserPages/stepTwo";
import StepThree from "./GuestUserPages/stepThree";
import { useTranslation } from "react-i18next";
import { useAuth } from "context/auth-context";
import { getDataNew, getIpAddressByIPify } from "components/JoinNowForm/getdata";
import { SuccessImageComponent } from "components/LoginForm/SuccessImageComponent";
import { AuthLayout } from "components/LoginForm/AuthLayout";
import { GuestErrorModal, GuestStepper, VerifyBenefits } from "./GuestUserPages/GuestClaimParts";

const GuestSignupForm = ({ point }: any) => {

  const validateEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };
  const validateOtp = (otpString) => {
    return /^\d{6}$/.test(otpString);
  };
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { setAuth } = useAuth();
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [resendCountdown, setResendCountdown] = useState(0);
  const [resendLoading, setResendLoading] = useState(false);
  const [submitPassLoading, setSubmitPassLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [otpError, setOtpError] = useState("");
  const [submitError, setSubmitError] = useState("");
  const [sessionId, setSessionId] = useState<string>("");
  const [uid, setUid] = useState<string | null>(null);
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
  const [ipAddressName, setIpAddressName] = useState("");
  const [countryName, setCountryName] = useState("");
  const [searchParams] = useState(new URLSearchParams(window.location.search));
  const [form, setForm] = useState({
    languge: "",
    timeZone: "",
    country: "",
    state: "",
    currentUrl: "",
    referalId: "",
    sid: "",
    clickid: "",
    geo: "",
  });
  useEffect(() => {
    const initialize = async () => {
      const ipAddress = await getIpAddressByIPify();
      setIpAddressName(ipAddress);
    };

    initialize();
  }, []);

  const otpRefs = useRef([]);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const uidParam = params.get("UID");
    const sessionIdParam = params.get("SESSION_ID");

    if (uidParam) setUid(uidParam);
    if (sessionIdParam) setSessionId(sessionIdParam);
  }, []);

  useEffect(() => {
    const initialize = async () => {
      getDataNew(setIpAddressName, setCountryName, setForm, form, searchParams);
      const ipAddress = await getIpAddressByIPify();
      if (ipAddress) setIpAddressName(ipAddress);
    };
    initialize();
  }, []);

  // Step 1: Email verification
  const handleVerifyEmail = async (event) => {
    event.preventDefault();
    if (!email) {
      setEmailError("Email is required.");
      return;
    }
    if (!validateEmail(email)) {
      setEmailError("Please enter a valid email address.");
      return;
    }
    setEmailError("");
    setIsLoading(true);
    setOtp(["", "", "", "", "", ""]);
    const response = await insertOtpForExistEmailApi(email);
    setIsLoading(false);
    if (response?.success === false) {
      setSubmitError(response?.message || "Error sending OTP.");
    } else {
      toastrSuccess("OTP sent on your email");
      setSubmitError("");
      setStep(1.5);
      setResendCountdown(30);
      const interval = setInterval(() => {
        setResendCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(interval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      setTimeout(() => otpRefs.current[0]?.focus(), 100);
    }
  };
  const handleSubmitForLogin = async (type) => {
    try {
      // If uid or sessionId is missing, redirect to surveys
      if (!uid || !sessionId) {
        navigate("/my-surveys");
        return;
      }

      setIsLoading(true);

      // Update email of panelist
      const updateEmailResponse = await updateEmailOfPanelistApi(uid, email, sessionId, type, name, password, ipAddressName, countryName, form);
      if (!updateEmailResponse?.success) {
        // setErrorMessage(updateEmailResponse?.message || "Failed to update email.");
        setIsErrorModalOpen(true);
        return;
      }

      // Update password for the existing email
      const updateResponse = await updatePasswordForExistEmailApi(email, type, password);

      // An existing account is no longer signed in automatically: the backend
      // stopped issuing a session on that path (security incident 2026-08-26),
      // because it did so without ever checking a credential. Send the user to
      // log in normally instead of leaving them on a dead screen.
      if (updateResponse?.requiresLogin) {
        toastrSuccess(updateResponse?.message || "This email is already registered. Please sign in to continue.");
        navigate("/login", { state: location.state || "/dashboard" });
        return;
      }

      if (updateResponse?.success) {
        toastrSuccess(t("form.login.successful"));
        setAuth(updateResponse?.response.result);
        localStorage.setItem("access_token", updateResponse?.token);
        navigate(location.state || "/dashboard", { state: null });
        if (type === 'NEW_USER') {
          localStorage.setItem("joinQualPopup", "true");
          // setShowJoinQualification(true);
        }
      } else {
        navigate("/my-surveys");
      }

    } catch (error) {
      setOtpError("An unexpected error occurred. Please try again.");
      setIsErrorModalOpen(true);
    } finally {
      setIsLoading(false);
    }
  };

  // Collect OTP string from array
  const getOtpString = () => otp.join("");

  const handleOtpSubmit = async (event) => {
    event.preventDefault();
    const otpString = getOtpString();
    if (!validateOtp(otpString)) {
      setOtpError("OTP must be exactly 6 digits.");
      return;
    }
    setOtpError("");

    if (!email || !otpString) {
      setOtpError("Email and OTP are required.");
      return;
    }
    setIsLoading(true);
    try {
      const response = await verifyIsOtpAreValidateApi(email, otpString);

      if (response?.success) {
        const existUser = await checkUserExistOrNotApi(email);
        setOtp(["", "", "", "", "", ""]);
        if (existUser?.oldUser === 1) {
          await handleSubmitForLogin('EXISTING_USER');
          return;
        }
        toastrSuccess("Email verified successfully.");
        setSubmitError("");
        setStep(2);
        setEmail(email);
      } else {
        setOtp(["", "", "", "", "", ""]);
        setOtpError(response?.message || "OTP verification failed.");
        setIsErrorModalOpen(true);
        return;
      }
    } catch (error) {
      setOtpError("An unexpected error occurred. Please try again.");
      setIsErrorModalOpen(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResend = async () => {
    if (resendCountdown > 0 || resendLoading) return;

    setResendLoading(true);
    try {
      setOtp(["", "", "", "", "", ""]);
      const response = await insertOtpForExistEmailApi(email);
      if (response?.success) {
        toastrSuccess("OTP resent to your email!");
        setResendCountdown(30);
        const interval = setInterval(() => {
          setResendCountdown((prev) => {
            if (prev <= 1) {
              clearInterval(interval);
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      } else {
        setSubmitError(response?.message || "Failed to resend OTP.");
      }
    } catch (error) {
      setSubmitError("An unexpected error occurred. Please try again.");
    } finally {
      setResendLoading(false);
    }
  };


  const handleOtpChange = (value, index) => {
    if (!/^\d*$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    setOtpError("");

    if (value && index < 5) {
      otpRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpKeyDown = (e, index) => {
    if (e.key === "Backspace") {
      if (otp[index] === "") {
        if (index > 0) {
          otpRefs.current[index - 1]?.focus();
        }
      }
    }
    if (e.key === "Enter") {
      e.preventDefault();
      handleOtpSubmit(e);
    }
  };

  const handleSubmitName = (e) => {
    e.preventDefault();

    // Trimmed value
    const trimmedName = name.trim();

    if (!trimmedName) {
      setSubmitError("Name cannot be empty or only spaces.");
      return;
    }

    if (name.startsWith(" ")) {
      setSubmitError("Name cannot start with a space.");
      return;
    }

    setSubmitError("");
    setStep(3);
  };

  const handleSubmitPassword = async (e) => {
    e.preventDefault();
    if (!password || !confirmPassword) {
      setSubmitError("Please fill both password fields.");
      return;
    }
    if (password.length < 6) {
      setSubmitError("Password must be at least 6 characters long.");
      return;
    }
    if (password !== confirmPassword) {
      setSubmitError("Passwords do not match.");
      return;
    }
    setSubmitError("");
    // toastrSuccess("Account created successfully!");
    setSubmitPassLoading(true);
    await handleSubmitForLogin('NEW_USER');
    setSubmitPassLoading(false);
  };


  return (
    <>
      <AuthLayout aside={<SuccessImageComponent point={point} />}>
        <section className="zs-card w-full max-w-[460px] px-6 pt-7 pb-8 animate-fadeInUp sm:px-9 sm:pt-9 sm:pb-9">
          <p className="zs-eyebrow">Claim your reward</p>
          <h1 className="font-display text-2xl font-bold leading-tight tracking-tight text-ink">
            Verify Your Email
          </h1>
          <p className="mt-2 mb-6 text-sm leading-relaxed text-ink-3">
            Enter your email address to verify your reward and continue.
          </p>

          <GuestStepper step={step} />

          <VerifyBenefits />

          <div>
            {step === 1 && (
              <StepOne email={email} setEmail={setEmail} emailError={emailError} submitError={submitError} handleVerifyEmail={handleVerifyEmail} isLoading={isLoading} />
            )}

            {step === 1.5 && (
              <StepOneHalf otp={otp} handleOtpChange={handleOtpChange} handleOtpKeyDown={handleOtpKeyDown} otpRefs={otpRefs} otpError={otpError} submitError={submitError} handleResend={handleResend} resendCountdown={resendCountdown} resendLoading={resendLoading} setStep={setStep} handleOtpSubmit={handleOtpSubmit} isLoading={isLoading} />
            )}

            {step === 2 && (
              <StepTwo name={name} setName={setName} setStep={setStep} submitError={submitError} handleSubmitName={handleSubmitName} />
            )}

            {step === 3 && (
              <StepThree password={password} setPassword={setPassword} confirmPassword={confirmPassword} setConfirmPassword={setConfirmPassword} submitError={submitError} setStep={setStep} handleSubmitPassword={handleSubmitPassword} submitPassLoading={submitPassLoading} />
            )}

            {isErrorModalOpen && (
              <GuestErrorModal message={otpError || submitError} onClose={() => setIsErrorModalOpen(false)} />
            )}
          </div>
        </section>
      </AuthLayout>
    </>
  );
};

export default GuestSignupForm;
